import { ISGLayerIds } from "fond/types/layer";

import { ValidationFunction } from "./GenericValidator";
import { makeValidateDemand, makeValidateLeadIn, makeValidateUgPath, makeValidateUgPoint } from "./undergroundValidation";

/**
 * Validation function for ISG demand points.
 *
 * @see makeValidateDemand
 */
export const validateDemand = makeValidateDemand({ leadinsLayerId: ISGLayerIds.leadins });

/**
 * Validation function for ISG leadIn features.
 *
 * A leadIn is valid if:
 * - One end (either vertex) is colocated with a demand point
 * - The other end is colocated with a ugPoint
 * - The order doesn't matter
 *
 * When a leadIn is moved, this validation will be triggered for:
 * - The leadIn itself
 * - Any demand/ugPoint features that were connected before the move
 * - Any demand/ugPoint features that are connected after the move
 */
export const validateLeadIn = makeValidateLeadIn({
  demandLayerId: ISGLayerIds.demand,
  ugPointsLayerId: ISGLayerIds.ugPoints,
});

/**
 * Validation function for ISG ugPoint features.
 *
 * A ugPoint is valid if:
 * - It has at least one leadIn or ugPath connected to it
 */
export const validateUgPoint = makeValidateUgPoint({
  ugPathLayerId: ISGLayerIds.ugPath,
  leadinsLayerId: ISGLayerIds.leadins,
});

/**
 * Validation function for ISG ugPath features.
 *
 * A ugPath is valid if:
 * - The first vertex is colocated with a ugPoint
 * - The last vertex is colocated with a ugPoint
 *
 * When a ugPath is moved, this validation will be triggered for:
 * - The ugPath itself
 * - Any ugPoint features that were connected before the move
 * - Any ugPoint features that are connected after the move
 */
export const validateUgPath = makeValidateUgPath({ ugPointsLayerId: ISGLayerIds.ugPoints });

/**
 * Validation function for ISG hub features.
 *
 * A hub is valid if:
 * - It is colocated with a ugPoint
 *
 * When a hub is moved, the validation for the hub will be re-evaluated.
 */
export const validateHub: ValidationFunction = (feature, _allFeatures, helpers) => {
  const featureId = feature.id as string;
  const errors: string[] = [];

  // Check if this is a Point feature
  if (feature.geometry.type !== "Point") {
    return {
      featureId,
      valid: false,
      errors: ["Hub must be a Point feature"],
    };
  }

  const hubPosition = feature.geometry.coordinates;

  // Find all ugPoints colocated at this hub position
  const colocatedUgPoints = helpers.findFeaturesAtPosition(hubPosition, ISGLayerIds.ugPoints);

  if (colocatedUgPoints.length === 0) {
    errors.push("Hub must be colocated with a ugPoint");
  }

  return {
    featureId,
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Example: How to use these validation functions in DataPrepDraw
 *
 * @example
 * import { validateDemand, validateLeadIn, validateUgPoint, validateUgPath } from 'fond/draw/validation/isgValidation';
 *
 * const defaultOptions: MapboxDrawOptions = {
 *   allLayers: [
 *     {
 *       ID: ISGLayerIds.demand,
 *       GeometryType: "Point",
 *       validate: validateDemand,  // Add validation here
 *       getFeatureProperties: () => ({ layerId: ISGLayerIds.demand }),
 *     },
 *     {
 *       ID: ISGLayerIds.leadins,
 *       GeometryType: "LineString",
 *       validate: validateLeadIn,  // Add validation here
 *       options: { midPoints: false },
 *       getFeatureProperties: () => ({ layerId: ISGLayerIds.leadins }),
 *     },
 *     {
 *       ID: ISGLayerIds.ugPoints,
 *       GeometryType: "Point",
 *       validate: validateUgPoint,  // Add validation here
 *       getFeatureProperties: () => ({ layerId: ISGLayerIds.ugPoints }),
 *     },
 *     {
 *       ID: ISGLayerIds.ugPath,
 *       GeometryType: "LineString",
 *       validate: validateUgPath,  // Add validation here
 *       options: { midPoints: false },
 *       getFeatureProperties: () => ({ layerId: ISGLayerIds.ugPath }),
 *     },
 *   ],
 *   // ... other options
 * };
 */
